/**
 * 관리자 프로필 바꾸기 — $j.F.Admin.ProfileAvatar
 *
 * 헤더 드롭다운의 아바타를 누르면 열린다. 예전에는 그 클릭이 곧바로 파일선택창이었다 —
 * 사진이 없는 관리자는 회색 사람 아이콘 말고는 고를 것이 없었고, 여러 계정을 오가는
 * CS 팀은 지금 누구로 들어와 있는지 헤더만 보고는 알 수 없었다.
 *
 * 이제 이모지 얼굴 + 배경색을 고른다. 사진 올리기는 창 안에 그대로 남는다.
 *
 * 목록(얼굴·색)은 서버가 정본이다 — `App\Services\Admin\AdminAvatarService`.
 * 관리자 레이아웃이 데이터 블록으로 넘긴다(F/Admin/LayoutData 'avatar'). 여기 이모지를 적어 두면 DB 에 담긴 키와
 * 그림이 두 곳에서 갈린다.
 */
import Util from '../../Common/Util';
import LayoutData from './LayoutData';
export default class ProfileAvatar {

    constructor() {
        this.modal = null;
        this.face  = '';
        this.tone  = '';
    }

    /** 서버가 내려준 목록. 없으면 빈 판 (관리자 레이아웃 밖에서 부른 경우) */
    get catalog() {
        return LayoutData.get('avatar', { faces: {}, tones: {}, defaultTone: 'blue', fallback: { type: 'none' } });
    }

    /**
     * '아바타 안 고름' 칸에 그릴 것 — 사진이 있으면 그 사진, 없으면 자동 아바타.
     * 회색 사람을 그리면 안 된다: 사진이 없는 관리자는 해제해도 자동 아바타가 뜨므로
     * 창이 실제와 다른 얼굴을 보여주게 된다.
     */
    _fallbackHtml() {
        const f = this.catalog.fallback || { type: 'none' };
        if (f.type === 'photo') return `<img src="${f.url}" alt="">`;
        if (f.type === 'auto')  return `<span class="adm-avap__auto" style="--ava-bg:${f.bg};--ava-bg-dark:${f.bgDark}">${f.emoji}</span>`;
        return '<i class="fas fa-user"></i>';
    }

    /** 해제 칸의 설명 — 무엇으로 돌아가는지 말한다 */
    _fallbackTitle() {
        const t = (this.catalog.fallback || {}).type;
        if (t === 'photo') return '올려둔 사진 사용';
        if (t === 'auto')  return '자동 아바타 사용 (회원번호로 정해집니다)';
        return '기본 이미지 사용';
    }

    /**
     * 고르기 창을 연다.
     * @param {string} current 지금 값 ('cat/blue' 또는 빈값)
     */
    open(current) {
        const c = this.catalog;
        const [face, tone] = String(current || '').split('/');
        this.face = face || '';
        this.tone = (tone && c.tones[tone]) ? tone : c.defaultTone;

        this.modal = $j.Ui.Modal.open({
            id: 'adm-ava-modal',
            title: '프로필 바꾸기',
            width: '400px',
            html: this._body(),
        });

        const $el = this.modal.el;
        $el.on('click', '.adm-avap__face', (e) => {
            this.face = $(e.currentTarget).data('face') || '';
            this._sync();
        });
        $el.on('click', '.adm-avap__tone', (e) => {
            this.tone = $(e.currentTarget).data('tone');
            this._sync();
        });
        $el.on('click', '.adm-avap__photo', () => this.pickPhoto());
        $el.on('click', '.adm-avap__save',  () => this.save());

        this._sync();
    }

    _body() {
        const c = this.catalog;

        // 해제 칸 — 아바타를 풀었을 때 보이는 것을 그대로 그린다. 얼굴 칸과 같은 줄에 둔다
        // (따로 [해제] 버튼을 만들면 무엇으로 돌아가는지 안 보인다).
        let faces = `<button type="button" class="adm-avap__face is-fallback" data-face=""
                             title="${this._fallbackTitle()}">${this._fallbackHtml()}</button>`;
        faces += Object.keys(c.faces)
            .map((k) => `<button type="button" class="adm-avap__face" data-face="${k}">${c.faces[k]}</button>`)
            .join('');

        const tones = Object.keys(c.tones)
            .map((k) => `<button type="button" class="adm-avap__tone" data-tone="${k}"
                                 style="background:${c.tones[k][0]}"></button>`)
            .join('');

        return `
<div class="adm-avap">
  <div class="adm-avap__preview"><span class="adm-avap__ava" id="admAvaPreview"></span></div>
  <div class="adm-avap__grid">${faces}</div>
  <div class="adm-avap__tones">${tones}</div>
  <div class="adm-avap__foot">
    <button type="button" class="btn btn-outline-secondary adm-avap__photo">
      <i class="fas fa-camera"></i> 사진 올리기
    </button>
    <button type="button" class="btn btn-primary adm-avap__save">저장</button>
  </div>
</div>`;
    }

    /** 고른 것을 미리보기·선택표시에 반영 */
    _sync() {
        const c = this.catalog;
        const $el = this.modal.el;

        $el.find('.adm-avap__face').each((i, b) => {
            $(b).toggleClass('is-on', ($(b).data('face') || '') === this.face);
        });
        $el.find('.adm-avap__tone').each((i, b) => {
            $(b).toggleClass('is-on', $(b).data('tone') === this.tone);
        });

        // 얼굴을 안 고른 상태(=사진 쓰기)면 색은 의미가 없다
        $el.find('.adm-avap__tones').toggleClass('is-off', !this.face);

        const $p = $el.find('#admAvaPreview');
        if (this.face) {
            $p.html(c.faces[this.face]).css('background', c.tones[this.tone][0]).removeClass('is-fallback');
        } else {
            $p.html(this._fallbackHtml()).css('background', '').addClass('is-fallback');
        }
    }

    /** 지금 고른 값 ('cat/blue' 또는 빈값 = 아바타 없음) */
    value() {
        return this.face ? `${this.face}/${this.tone}` : '';
    }

    save() {
        $.ajax({
            type: 'POST',
            url: '/adm/ajax/manager/avatar',
            data: { avatar: this.value() },
            success: (r) => {
                if (r.result !== true) { Util.swalert(r.message || '저장하지 못했습니다.'); return; }
                location.reload();
            },
            error: () => Util.swalert('저장 중 오류가 발생했습니다.'),
        });
    }

    /**
     * 사진 올리기 — 종전 흐름(공용 업로더 → upload:image → /adm/ajax/manager/profile-image).
     * 업로드가 끝나면 서버가 아바타를 풀고 화면을 새로 불러온다.
     */
    pickPhoto() {
        this.modal && this.modal.close();
        $j.F.Upload.addPhotoHere({ tag: 'profile', context: 'adminProfile', max_sheet: 1 });
    }
}
